/** Logical canvas for Memory minigame (authoring space). */
export const REWARD_MEMORY_LOGICAL_WIDTH = 1920;
export const REWARD_MEMORY_LOGICAL_HEIGHT = 1080;

export const REWARD_MEMORY_BACKGROUND_SRC = '/minigames/memory.jpg';
export const REWARD_MEMORY_CARD_BACK_SRC = '/minigames/memory_cardback.png';
/** Native pixels of `memory_cardback.png` — grid cells use this aspect ratio. */
export const REWARD_MEMORY_CARD_BACK_WIDTH = 1024;
export const REWARD_MEMORY_CARD_BACK_HEIGHT = 1024;
export const REWARD_MEMORY_GRID_COLS = 4;
export const REWARD_MEMORY_GRID_ROWS = 4;
export const REWARD_MEMORY_GRID_GAP_PX = 8;
/** Visual scale applied to the largest grid size that fits the arena. */
export const REWARD_MEMORY_CARD_SIZE_SCALE = 1;
/** Hard cap so cards do not dominate the playfield on large viewports. */
export const REWARD_MEMORY_MAX_CARD_SIZE_PX = 156;
/**
 * Symmetric inset of visible gold card art inside `memory_cardback.png` (opaque bounds ≈ 10.6%).
 * Face-up cards use a panel of this size (dark fill + reward icon).
 */
export const REWARD_MEMORY_CARD_ART_INSET_RATIO = 0.106;
/** Face panel fill when a card is flipped (matches reward art mood, hides card back). */
export const REWARD_MEMORY_CARD_FACE_BACKGROUND = '#162a47';
/** Fraction of play arena reserved above the card grid (HUD). */
export const REWARD_MEMORY_GRID_TOP_INSET_RATIO = 0.1;
export const REWARD_MEMORY_GRID_BOTTOM_INSET_RATIO = 0.025;
export const REWARD_MEMORY_GRID_SIDE_INSET_RATIO = 0.025;

/**
 * Largest square card size (px) that fits a 4×4 grid in the arena.
 * @param {number} arenaWidth
 * @param {number} arenaHeight
 * @returns {number}
 */
export function computeRewardMemoryCardSizePx(arenaWidth, arenaHeight) {
  if (!Number.isFinite(arenaWidth) || !Number.isFinite(arenaHeight) || arenaWidth < 1 || arenaHeight < 1) {
    throw new Error(`computeRewardMemoryCardSizePx: invalid arena size (${arenaWidth}, ${arenaHeight})`);
  }
  const availW =
    arenaWidth * (1 - 2 * REWARD_MEMORY_GRID_SIDE_INSET_RATIO) -
    (REWARD_MEMORY_GRID_COLS - 1) * REWARD_MEMORY_GRID_GAP_PX;
  const availH =
    arenaHeight * (1 - REWARD_MEMORY_GRID_TOP_INSET_RATIO - REWARD_MEMORY_GRID_BOTTOM_INSET_RATIO) -
    (REWARD_MEMORY_GRID_ROWS - 1) * REWARD_MEMORY_GRID_GAP_PX;
  const byWidth = availW / REWARD_MEMORY_GRID_COLS;
  const byHeight = availH / REWARD_MEMORY_GRID_ROWS;
  const fitted = Math.floor(Math.min(byWidth, byHeight) * REWARD_MEMORY_CARD_SIZE_SCALE);
  const cardSize = Math.min(fitted, REWARD_MEMORY_MAX_CARD_SIZE_PX);
  if (cardSize < 24) {
    throw new Error(`computeRewardMemoryCardSizePx: arena too small (${arenaWidth}×${arenaHeight})`);
  }
  return cardSize;
}

/**
 * @param {number} cardSizePx
 * @returns {{ width: number, height: number }}
 */
export function computeRewardMemoryCardSlotSizePx(cardSizePx) {
  if (!Number.isFinite(cardSizePx) || cardSizePx < 1) {
    throw new Error(`computeRewardMemoryCardSlotSizePx: invalid cardSizePx (${String(cardSizePx)})`);
  }
  const aspect = REWARD_MEMORY_CARD_BACK_WIDTH / REWARD_MEMORY_CARD_BACK_HEIGHT;
  if (!Number.isFinite(aspect) || aspect <= 0) {
    throw new Error(`computeRewardMemoryCardSlotSizePx: invalid card back aspect (${aspect})`);
  }
  return {
    width: cardSizePx,
    height: Math.max(1, Math.round(cardSizePx / aspect)),
  };
}

export const REWARD_MEMORY_FLIP_SOUND_SRC = '/minigames/flip.mp3';
export const REWARD_MEMORY_MATCH_SOUND_SRC = '/minigames/matching_cards.mp3';

/** Server `minigames.rewardMemory.pairCount` × 2 */
export const REWARD_MEMORY_CARD_COUNT = 16;
/** Server `minigames.rewardMemory.maxAttempts` */
export const REWARD_MEMORY_MAX_ATTEMPTS = 5;
/** Server `minigames.rewardMemory.maxRewardsPerGame` */
export const REWARD_MEMORY_MAX_REWARDS_PER_GAME = 4;

/** `pickedTreeIndex` sent to complete API when at least one pair matched. */
export const REWARD_MEMORY_WIN_PICK_VALUE = 1;
/** `pickedTreeIndex` sent when attempts are exhausted with no matches. */
export const REWARD_MEMORY_LOSS_PICK_VALUE = 0;

/** Ms to show a non-matching pair before flipping face-down again. */
export const REWARD_MEMORY_MISMATCH_HIDE_MS = 750;
/** CSS flip transition duration (`reward-memory-card-flipper`). */
export const REWARD_MEMORY_FLIP_TRANSITION_MS = 400;
